import { Plus } from "lucide-react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useSearchParams } from "react-router";

import { useManualTime, useTimesheet, type ManualTimeStatus } from "@/api/queries";
import { isStaff, useCurrentUser } from "@/app/auth";
import { PageHeader } from "@/components/common/PageHeader";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Segmented } from "@/components/ui/segmented";
import { dateInZone, shiftDate, weekStartOf } from "@/lib/format";

import { ManualTimeDialog } from "./ManualTimeDialog";
import { ManualTimeList } from "./ManualTimeList";
import { TimesheetTabs } from "./TimesheetTabs";

type View = "mine" | "review";
type StatusFilter = "pending" | "all";

/** The caller's manual time requests and, for staff, the ones waiting for them. */
export function ManualTimePage() {
  const { t } = useTranslation();
  const me = useCurrentUser();
  const staff = isStaff(me);
  const [params, setParams] = useSearchParams();
  const view: View = staff && params.get("view") === "review" ? "review" : "mine";
  const status: StatusFilter = (params.get("status") ??
    (view === "review" ? "pending" : "all")) as StatusFilter;
  const today = dateInZone(new Date(), me.timezone);
  const week = weekStartOf(today);
  // The current week of the caller tells whether and where they may add time.
  const sheet = useTimesheet(undefined, week, shiftDate(week, 6));
  const list = useManualTime({
    view,
    status: status === "pending" ? ("pending" as ManualTimeStatus) : undefined,
  });
  const [adding, setAdding] = useState(false);

  const set = (next: { view?: View; status?: StatusFilter }) => {
    const p = new URLSearchParams();
    const v = next.view ?? view;
    if (v === "review") p.set("view", "review");
    const s = next.status ?? (next.view ? undefined : status);
    if (s && s !== (v === "review" ? "pending" : "all")) p.set("status", s);
    setParams(p, { replace: true });
  };

  return (
    <>
      <PageHeader
        title={t("nav.timesheets")}
        actions={
          <>
            {staff && (
              <Segmented
                aria-label={t("manual.view")}
                value={view}
                onChange={(v) => set({ view: v })}
                options={[
                  { value: "mine", label: t("manual.views.mine") },
                  { value: "review", label: t("manual.views.review") },
                ]}
              />
            )}
            <Segmented
              aria-label={t("manual.statusFilter")}
              value={status}
              onChange={(s) => set({ status: s })}
              options={[
                { value: "pending", label: t("manual.filters.pending") },
                { value: "all", label: t("manual.filters.all") },
              ]}
            />
            {sheet.data?.can_add_time && (
              <Button onClick={() => setAdding(true)}>
                <Plus /> {t("manual.add.button")}
              </Button>
            )}
          </>
        }
      />
      <TimesheetTabs />
      <Card className="overflow-hidden">
        <p className="px-5 pt-4 pb-3 text-muted">
          {view === "review" ? t("manual.reviewHint") : t("manual.mineHint")}
        </p>
        <ManualTimeList
          items={list.data}
          timeZone={me.timezone}
          showUser={view === "review"}
          empty={
            view === "review"
              ? { title: t("manual.emptyReview") }
              : { title: t("manual.emptyMine"), description: t("manual.emptyMineHint") }
          }
        />
      </Card>
      {adding && sheet.data && (
        <ManualTimeDialog
          timeZone={sheet.data.timezone}
          projects={sheet.data.projects}
          defaultDate={today}
          approvalRequired={sheet.data.approval_required}
          onClose={() => setAdding(false)}
        />
      )}
    </>
  );
}
